// src/services/pipeline/MeasurementPipeline.ts
//
// オーケストレーション層(3章のアーキテクチャ図における中心コンポーネント)。
// UIコンポーネント(Measurement.tsx)は本クラスの薄いイベントリスナーになるだけで、
// 実際の映像取得・推論・数値計算はすべて各Workerに委譲する
// (「メインスレッド/UIスレッドでは重い処理を一切行わない」という3章の原則)。
//
// このクラス自身はメッセージの中継とステートマシン管理のみを行うため、
// 実行スレッド(メインスレッド上のプレーンなTSクラス)自体が重くなることはない。
// 将来的により厳密にオフメインスレッド化したい場合、本クラスをそのまま
// 専用のorchestrator.worker.tsへ移設しても公開インターフェースは変わらない。

import type { BiomechResult } from "../../workers/kinematics.worker";
import type { VisionFrameResult } from "../../workers/vision.worker";
import type { SessionSummaryForAdvice } from "../../workers/slm.worker";
import { detectDeviceTier, recommendedCameraConstraints, type Tier } from "../deviceTier";

export type PipelineState =
  | "idle"
  | "requesting_camera"
  | "camera_ready"
  | "no_person_detected"
  | "multi_person_detected"
  | "low_light_warning"
  | "measuring"
  | "generating_advice"
  | "error";

export interface FrameUpdate {
  biomech: BiomechResult;
  timestampMs: number;
}

export interface PipelineCallbacks {
  onStateChange?: (state: PipelineState) => void;
  onFrameUpdate?: (update: FrameUpdate) => void;
  onVisionFrame?: (keypointsFlat: Float32Array, width: number, height: number) => void;
  onAdviceReady?: (advice: string, mode: "llm" | "template") => void;
  onError?: (message: string) => void;
}

const LOW_LIGHT_THRESHOLD = 0.18; // meanBrightness(0-1)がこれを下回ると警告(9章)
const NO_PERSON_TIMEOUT_MS = 2500;

export class MeasurementPipeline {
  private visionWorker: Worker | null = null;
  private kinematicsWorker: Worker | null = null;
  private slmWorker: Worker | null = null;

  private state: PipelineState = "idle";
  private tier: Tier = 3;
  private stream: MediaStream | null = null;
  private videoEl: HTMLVideoElement | null = null;
  private rafId: number | null = null;
  private lastFrameTimeMs = 0;
  private lastPersonSeenAtMs = 0;

  private repMetrics: { compression: number[]; shear: number[]; kneeVarus: number[] } = {
    compression: [],
    shear: [],
    kneeVarus: [],
  };

  constructor(private callbacks: PipelineCallbacks) {}

  private setState(state: PipelineState) {
    this.state = state;
    this.callbacks.onStateChange?.(state);
  }

  async initialize(params: {
    heightM: number;
    massKg: number;
    sex: "male" | "female";
    externalLoadKg: number;
    /** 自前ホストするMediaPipe/WebLLMモデル資産のベースパス(任意)。
     *  省略時は各ワーカーが公式CDN/Hugging Faceの既定値を使う。 */
    modelBaseUrl?: string;
  }) {
    const tierInfo = await detectDeviceTier();
    this.tier = tierInfo.tier;

    this.visionWorker = new Worker(
      new URL("../../workers/vision.worker.ts", import.meta.url),
      { type: "module" },
    );
    this.kinematicsWorker = new Worker(
      new URL("../../workers/kinematics.worker.ts", import.meta.url),
      { type: "module" },
    );
    this.slmWorker = new Worker(
      new URL("../../workers/slm.worker.ts", import.meta.url),
      { type: "module" },
    );

    this.kinematicsWorker.onmessage = (e) => this.handleKinematicsMessage(e.data);
    this.visionWorker.onmessage = (e) => this.handleVisionMessage(e.data);
    this.slmWorker.onmessage = (e) => this.handleSlmMessage(e.data);

    this.visionWorker.postMessage({
      type: "init",
      tier: this.tier,
      wasmModelBasePath: params.modelBaseUrl,
    });
    this.kinematicsWorker.postMessage({
      type: "init",
      heightM: params.heightM,
      massKg: params.massKg,
      sex: params.sex,
      externalLoadKg: params.externalLoadKg,
    });
    this.slmWorker.postMessage({
      type: "init",
      tier: this.tier,
      modelBaseUrl: params.modelBaseUrl,
    });
  }

  async startCamera(videoEl: HTMLVideoElement) {
    this.setState("requesting_camera");
    this.videoEl = videoEl;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: recommendedCameraConstraints(this.tier),
        audio: false,
      });
      videoEl.srcObject = this.stream;
      await videoEl.play();
      this.setState("camera_ready");
      this.lastPersonSeenAtMs = performance.now();
      this.loop();
    } catch (err) {
      // 9章: カメラ権限拒否・デバイス競合時のフォールバック文言はUI側(Measurement.tsx)で表示
      this.setState("error");
      this.callbacks.onError?.(
        err instanceof Error ? err.message : "camera_access_failed",
      );
    }
  }

  private loop = () => {
    if (!this.videoEl || !this.visionWorker) return;
    const now = performance.now();
    // Tier 3は15fps程度に間引いて負荷を抑える(8章)
    const minIntervalMs = this.tier === 3 ? 66 : this.tier === 2 ? 41 : 33;
    if (now - this.lastFrameTimeMs >= minIntervalMs) {
      this.lastFrameTimeMs = now;
      createImageBitmap(this.videoEl)
        .then((bitmap) => {
          this.visionWorker?.postMessage(
            { type: "frame", frame: bitmap, timestampMs: now },
            [bitmap],
          );
        })
        .catch(() => {
          /* フレームキャプチャ失敗は無視して次フレームへ(9章) */
        });
    }
    this.rafId = requestAnimationFrame(this.loop);
  };

  private handleVisionMessage(data: VisionFrameResult | { type: "ready" }) {
    if (data.type === "ready") return;

    const now = performance.now();

    if (data.meanBrightness < LOW_LIGHT_THRESHOLD) {
      this.setState("low_light_warning");
    }

    if (data.candidates.length > 1 && this.state !== "measuring") {
      this.setState("multi_person_detected");
    }

    if (!data.primaryKeypointsFlat) {
      if (now - this.lastPersonSeenAtMs > NO_PERSON_TIMEOUT_MS) {
        this.setState("no_person_detected");
      }
      return;
    }

    this.lastPersonSeenAtMs = now;
    if (this.state !== "measuring") {
      this.setState("measuring");
    }

    // オーバーレイ描画用にコピーを渡す(元バッファはこの後kinematics.workerへtransferするため)
    if (this.videoEl) {
      this.callbacks.onVisionFrame?.(
        data.primaryKeypointsFlat.slice(),
        this.videoEl.videoWidth,
        this.videoEl.videoHeight,
      );
    }

    const dt = this.lastKinematicsTimestampMs
      ? (data.timestampMs - this.lastKinematicsTimestampMs) / 1000
      : 1 / 30;
    this.lastKinematicsTimestampMs = data.timestampMs;

    this.kinematicsWorker?.postMessage(
      {
        type: "processFrame",
        keypointsFlat: data.primaryKeypointsFlat,
        deltaTSeconds: dt,
        timestampMs: data.timestampMs,
      },
      [data.primaryKeypointsFlat.buffer],
    );
  }

  private lastKinematicsTimestampMs = 0;

  private handleKinematicsMessage(data: {
    type: string;
    result?: BiomechResult;
    timestampMs?: number;
    message?: string;
  }) {
    if (data.type === "biomechResult" && data.result) {
      this.repMetrics.compression.push(data.result.l4l5_compression_n);
      this.repMetrics.shear.push(data.result.l4l5_shear_n);
      this.repMetrics.kneeVarus.push(data.result.knee_varus_moment_nm);
      this.callbacks.onFrameUpdate?.({
        biomech: data.result,
        timestampMs: data.timestampMs ?? performance.now(),
      });
    } else if (data.type === "error") {
      this.callbacks.onError?.(data.message ?? "unknown_kinematics_error");
    }
  }

  private handleSlmMessage(data: {
    type: string;
    advice?: string;
    mode?: "llm" | "template";
  }) {
    if (data.type === "adviceResult" && data.advice) {
      this.callbacks.onAdviceReady?.(data.advice, data.mode ?? "template");
    }
  }

  finishSession(summary: Omit<SessionSummaryForAdvice, "maxCompressionN" | "maxShearN" | "maxKneeVarusNm">) {
    this.setState("generating_advice");
    const max = (arr: number[]) => (arr.length ? Math.max(...arr) : 0);
    this.slmWorker?.postMessage({
      type: "generate",
      requestId: Date.now(),
      summary: {
        ...summary,
        maxCompressionN: max(this.repMetrics.compression),
        maxShearN: max(this.repMetrics.shear),
        maxKneeVarusNm: max(this.repMetrics.kneeVarus),
      } satisfies SessionSummaryForAdvice,
    });
  }

  stop() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.stream?.getTracks().forEach((t) => t.stop());
    this.setState("idle");
  }

  dispose() {
    this.stop();
    this.visionWorker?.terminate();
    this.kinematicsWorker?.terminate();
    this.slmWorker?.terminate();
  }

  getRepSummaryArrays() {
    return this.repMetrics;
  }
}
